import React, { useState, useEffect } from 'react';
import WebsiteHeader from './WebsiteHeader';
import WebsiteFooter from './WebsiteFooter';
import WebsiteHome from './WebsiteHome';
import WebsiteUsedCars from './WebsiteUsedCars';
import WebsiteSpareParts from './WebsiteSpareParts';
import WebsiteAbout from './WebsiteAbout';
import WebsiteContact from './WebsiteContact';
import WebsiteUnitDetail from './WebsiteUnitDetail';
import Login from '../Login';
import WebsiteTradeApply from './WebsiteTradeApply';
import WebsiteRegister from './WebsiteRegister';
import { getContact, refreshContent } from './websiteStyles';

const PAPER = '#FAFAF8';
const heading = '"Inter Tight", sans-serif';

function WebsiteApp({ initialPage, onLogin, loginHandler, timedOut }) {
  const [page, setPage] = useState(initialPage || 'website');
  const [viewUnit, setViewUnit] = useState(null);

  const [, forceUpdate] = useState();
  // Show cached content instantly, refresh from Supabase in background
  useEffect(() => { refreshContent(() => forceUpdate({})); }, []);
  const contact = getContact();

  const navigate = (p) => {
    setPage(p);
    setViewUnit(null);
    window.scrollTo(0, 0);
    window.history.replaceState({}, '', p === 'website' ? '/' : `?page=${p}`);
  };

  // Header sign-in: navigate to login page
  const handleSignIn = () => {
    if (loginHandler) {
      setPage('website-login');
      window.scrollTo(0, 0);
    } else {
      // No loginHandler yet — redirect to login URL directly
      window.history.replaceState({}, '', '?page=login');
      window.location.reload();
    }
  };

  const openUnit = (unit) => {
    setViewUnit(unit);
    window.scrollTo(0, 0);
  };

  // Unit detail view
  if (viewUnit) {
    return (
      <div style={{ minHeight: '100vh', background: PAPER, fontFamily: heading }}>
        <WebsiteHeader currentPage={page} onNavigate={(p) => { setViewUnit(null); navigate(p); }} onLogin={handleSignIn} />
        <WebsiteUnitDetail unit={viewUnit} onBack={() => setViewUnit(null)} onNavigate={navigate} onViewUnit={openUnit} />
        <WebsiteFooter onNavigate={(p) => { setViewUnit(null); navigate(p); }} />
        <FloatingWhatsApp whatsapp={contact.whatsapp} />
      </div>
    );
  }

  // Login page (rendered inside website layout with header)
  const isLoginPage = page === 'website-login';

  return (
    <div style={{ minHeight: '100vh', background: PAPER, fontFamily: heading }}>
      <WebsiteHeader currentPage={page} onNavigate={navigate} onLogin={handleSignIn} />
      {isLoginPage && loginHandler && (
        <Login onLogin={loginHandler} timedOut={timedOut} />
      )}
      {!isLoginPage && page === 'website' && <WebsiteHome onNavigate={navigate} onViewUnit={openUnit} />}
      {!isLoginPage && (page === 'website-used-cars' || page === 'website-inventory') && <WebsiteUsedCars onViewUnit={openUnit} />}
      {!isLoginPage && page === 'website-spare-parts' && <WebsiteSpareParts onViewUnit={openUnit} />}
      {!isLoginPage && page === 'website-about' && <WebsiteAbout />}
      {!isLoginPage && page === 'website-contact' && <WebsiteContact />}
      {!isLoginPage && page === 'website-trade-apply' && <WebsiteTradeApply />}
      {!isLoginPage && page === 'website-register' && <WebsiteRegister onNavigate={navigate} onGoToLogin={handleSignIn} />}
      {!isLoginPage && page !== 'website-register' && <WebsiteFooter onNavigate={navigate} />}
      <FloatingWhatsApp whatsapp={contact.whatsapp} />
    </div>
  );
}

/* ── Floating WhatsApp button (bottom-left, all pages) ── */
function FloatingWhatsApp({ whatsapp }) {
  const msg = encodeURIComponent('Hello, I\'m interested in your vehicles/parts. Can you help me?');
  return (
    <a href={`https://wa.me/${whatsapp}?text=${msg}`} target="_blank" rel="noopener noreferrer"
      style={{
        position: 'fixed', bottom: 24, left: 24, zIndex: 999,
        width: 56, height: 56, borderRadius: '50%', background: '#25D366',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 4px 16px rgba(37,211,102,0.4)', transition: 'transform 0.2s, box-shadow 0.2s',
        textDecoration: 'none',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.08)'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(37,211,102,0.5)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 4px 16px rgba(37,211,102,0.4)'; }}>
      <svg viewBox="0 0 24 24" fill="#fff" style={{ width: 28, height: 28 }}>
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
        <path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.5.5 0 00.612.616l4.529-1.476A11.96 11.96 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22a9.94 9.94 0 01-5.33-1.543l-.372-.225-2.688.877.858-2.63-.248-.393A9.96 9.96 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
      </svg>
    </a>
  );
}

export default WebsiteApp;
